import * as React from "react";
import * as _ from "underscore";
import { connect } from "react-redux";
import { DocShape, DocTypes } from "@eave/shared/types/api";
import { Button } from "carbon-components-react";
import { Save16, Download24 } from "@carbon/icons-react";

import HurdleView from "underwriter_dashboard/shared/HurdleView";
import moment from "moment";
import { HurdleProps } from "underwriter_dashboard/HurdleEditor";
import Money from "../../Money";
import LabeledInput from "../../LabeledInput";
import APIClient from "../../APIClient";
import { getFileDownloadBaseURL } from "../../../config";
import MaskedAccountNumber from "../shared/MaskedAccountNumber";
import { mapDispatchToProps } from "./selectors";

const { FormattedDate } = require("react-intl");
const { Table } = require("react-bootstrap");
const styles = require("underwriter_dashboard/hurdle_editors/styles.module.scss");

interface HurdleEditorProps extends HurdleProps {
  loadApp: Function;
}

class FundingHurdleEditor extends React.Component<HurdleEditorProps, any> {
  static initialState(props: HurdleEditorProps) {
    const { wire_status, documents } = props.app;
    const origin_tapes = _.filter(documents, doc => doc.type === DocTypes.OriginWarehouseTape);
    let s3_key;
    let wire_created_date;
    if (origin_tapes.length) {
      const latest = _.max(origin_tapes, doc => +moment(doc.created_at)) as DocShape;
      s3_key = latest.s3_key;
      wire_created_date = latest.created_at;
    }
    return {
      s3_key,
      wire_created_date,
      wire_status: wire_status.status,
      wire_instructions_received: wire_status.wire_instructions_received,
      receipt_confirmed_by: wire_status.receipt_confirmed_by,
      federal_reference_confirmation_number: wire_status.federal_reference_confirmation_number,
      settlement_agent_receipt: wire_status.settlement_agent_receipt,
      wire_deductions: [],
    };
  }

  constructor(props: HurdleEditorProps) {
    super(props);
    this.state = FundingHurdleEditor.initialState(props);
  }

  componentDidMount() {
    APIClient.get(`/${this.props.app.id}/wire_deductions`).then((response: any) => {
      this.setState({
        wire_deductions: response.wire_deductions,
        total_deduction: response.total_deduction,
        wire_amount: response.wire_amount,
      });
    });
  }

  onOptionChange(target, option) {
    this.onChange(target, option.value);
  }

  onBooleanChange(e) {
    const { name, checked } = e.target;

    this.setState({
      ...this.state,
      [name]: checked || false,
    });
  }

  onChange(target, value) {
    this.setState({
      [target]: value,
    });
  }

  onSave() {
    const { app, loadApp } = this.props;

    const {
      federal_reference_confirmation_number,
      receipt_confirmed_by,
      settlement_agent_receipt,
      wire_instructions_received,
      wire_status,
    } = this.state;

    APIClient.post(`/loan_applications/${app.id}/wire_status`, {
      federal_reference_confirmation_number,
      receipt_confirmed_by,
      settlement_agent_receipt,
      wire_instructions_received,
      wire_status,
    }).then(() => loadApp(app.id));
  }

  generateCSV() {
    const { app, loadApp } = this.props;
    this.setState({ s3_key: null });
    APIClient.get(`/${app.id}/origin_csv`, response => {
      this.setState({ s3_key: response.s3_key });
    }).then(() => loadApp(app.id));
  }

  render() {
    const { app, hurdle, onHide } = this.props;
    const {
      contacts,
      wire_status,
      computed_properties,
      active_rate,
      computed_properties: { purchase_price },
    } = app;
    const { settlement_agent, settlement_agent_company } = contacts;
    const { account_number, aba_number, reference_number, authorized_by } = wire_status;
    const { signing_date, disbursement_date, closing_date } = computed_properties;
    const rate_lock_expiration_date = active_rate && active_rate.expires_at;
    const {
      federal_reference_confirmation_number,
      receipt_confirmed_by,
      s3_key,
      settlement_agent_receipt,
      total_deduction,
      wire_amount,
      wire_created_date,
      wire_deductions,
      wire_instructions_received,
    } = this.state;
    const statusOptions = [
      { label: "None", value: "" },
      { label: "Funded", value: "StatusFunded" },
      { label: "Wire Approved", value: "StatusWireApproved" },
      { label: "Wire Requested", value: "StatusWireRequested" },
    ];
    return (
      <HurdleView app={app} hurdle={hurdle} onHide={onHide}>
        <div className="funding-hurdle dashboard-container">
          <div className="col-md-6">
            <Table condensed>
              <tbody>
                <tr>
                  <td>Wire Instructions Received</td>
                  <td>
                    <LabeledInput
                      inputType="boolean"
                      inputName="wire_instructions_received"
                      inputValue={wire_instructions_received}
                      checkboxLabel={wire_instructions_received ? "Received" : "Not Received"}
                      onChange={this.onBooleanChange.bind(this)}
                    />
                  </td>
                </tr>
                <tr>
                  <td>Wire Status</td>
                  <td>
                    <LabeledInput
                      inputType="select"
                      inputName="wire_status"
                      inputValue={this.state.wire_status}
                      onChange={this.onOptionChange.bind(this, "wire_status")}
                      options={statusOptions}
                    />
                  </td>
                </tr>
                <tr>
                  <td>ABA #</td>
                  <td>{aba_number}</td>
                </tr>
                <tr>
                  <td>Account #</td>
                  <td>
                    {account_number && <MaskedAccountNumber accountNumber={account_number} />}
                  </td>
                </tr>
                <tr>
                  <td>Closing / Signing Date</td>
                  <td>{signing_date && <FormattedDate value={signing_date} timeZone="UTC" />}</td>
                </tr>
                <tr>
                  <td>Close of Escrow / Recording Date</td>
                  <td>{closing_date && <FormattedDate value={closing_date} timeZone="UTC" />}</td>
                </tr>
                <tr>
                  <td>Disbursement Date</td>
                  <td>
                    {disbursement_date && (
                      <FormattedDate value={disbursement_date} timeZone="UTC" />
                    )}
                  </td>
                </tr>
                <tr>
                  <td>Rate Lock Expiration</td>
                  <td>
                    {rate_lock_expiration_date && (
                      <FormattedDate
                        value={rate_lock_expiration_date}
                        year="numeric"
                        month="numeric"
                        day="numeric"
                        hour="numeric"
                        minute="numeric"
                        second="numeric"
                        timeZoneName="short"
                        timeZone="America/Los_Angeles"
                      />
                    )}
                  </td>
                </tr>
                {/* TODO: This is a strange way to format this. */}
                <tr>
                  <td />
                  <td />
                </tr>
                <tr>
                  <td />
                  <td />
                </tr>
                <tr>
                  <td />
                  <td />
                </tr>
                <tr>
                  <td />
                  <td />
                </tr>
                <tr>
                  <td>Purchase Price</td>
                  <td>
                    <Money value={parseFloat(purchase_price)} />
                  </td>
                </tr>
                <tr>
                  <td>Escrow Account Required</td>
                  <td>Yes</td>
                </tr>
              </tbody>
            </Table>
          </div>
          <div className="col-md-6">
            <Table condensed>
              <tbody>
                <tr>
                  <td>Settlement Agent Company</td>
                  <td>{settlement_agent_company && settlement_agent_company.name}</td>
                </tr>
                <tr>
                  <td>Contact</td>
                  <td>{settlement_agent && settlement_agent.name}</td>
                </tr>
                <tr>
                  <td>Reference #</td>
                  <td>{reference_number}</td>
                </tr>
                <tr>
                  <td>Phone</td>
                  <td>{settlement_agent && settlement_agent.phone}</td>
                  <td>Ext.</td>
                  <td>TBD</td>
                </tr>
                <tr>
                  <td>Email</td>
                  <td>{settlement_agent && settlement_agent.email}</td>
                </tr>
                <tr>
                  <td>Order Number</td>
                  <td>TBD</td>
                </tr>
                <tr>
                  <td>Vendor</td>
                  <td>
                    {settlement_agent && settlement_agent.external_id_type === "ClosingCorpId"
                      ? "Closing Corp"
                      : "Borrower Chosen"}
                  </td>
                </tr>
                {/* TODO: This is a strange way to format this. */}
                <tr>
                  <td />
                  <td />
                </tr>
                <tr>
                  <td />
                  <td />
                </tr>
                <tr>
                  <td />
                  <td />
                </tr>
                <tr>
                  <td />
                  <td />
                </tr>
                <tr>
                  <td />
                  <td />
                </tr>
                <tr>
                  <td />
                  <td />
                </tr>
                <tr>
                  <td>Loan Amount</td>
                  <td>{app.computed_properties.loan_amount}</td>
                </tr>
              </tbody>
            </Table>
          </div>
          <div className="col-lg-6 col-lg-offset-6">
            <div className="col-lg-8">
              <Button size="lg" onClick={this.generateCSV.bind(this)}>
                Generate CSV For Releasing Funds
              </Button>
            </div>
            <div className="col-lg-4">
              {s3_key && (
                <a href={getFileDownloadBaseURL(s3_key)}>
                  <Button size="lg" renderIcon={Download24}>
                    Download latest
                  </Button>
                </a>
              )}
            </div>
          </div>
          <div className="col-lg-12">
            <Table condensed>
              <thead>
                <tr>
                  <th>Fee Description</th>
                  <th>P.O.C.</th>
                  <th>Amount Deducted From (Added To) Funds </th>
                </tr>
              </thead>
              <tbody>
                {wire_deductions &&
                  _.map(wire_deductions, (deduction: any, index) => (
                    <tr key={index}>
                      <td>{deduction.Description}</td>
                      <td>{deduction.PaidOutsideClosing ? "Yes" : "No"}</td>
                      <td>
                        <Money value={deduction.Amount} />
                      </td>
                    </tr>
                  ))}
                <tr>
                  <td />
                  <td>
                    <strong>Total Deducted from wire</strong>
                  </td>
                  <td>
                    <strong>
                      <Money value={total_deduction} />
                    </strong>
                  </td>
                </tr>
                <tr>
                  <td />
                  <td>
                    <strong>WIRE AMOUNT</strong>
                  </td>
                  <td>
                    <strong>
                      <Money value={wire_amount} />
                    </strong>
                  </td>
                </tr>
              </tbody>
            </Table>
          </div>
          <div className="col-lg-5">
            <Table condensed>
              <tbody>
                <tr>
                  <th>Federal Reference Confirmation Number</th>
                  <th>
                    <LabeledInput
                      inputName="federal_reference_confirmation_number"
                      inputValue={federal_reference_confirmation_number}
                      onChangeWithValue={this.onChange.bind(
                        this,
                        "federal_reference_confirmation_number",
                      )}
                    />
                  </th>
                </tr>
                <tr>
                  <th>Date of Wire</th>
                  <th>
                    {wire_created_date && (
                      <FormattedDate value={wire_created_date} timeZone="UTC" />
                    )}
                  </th>
                </tr>
                <tr>
                  <th>Authorized By</th>
                  <th>
                    {authorized_by && `${authorized_by.first_name} ${authorized_by.last_name}`}
                  </th>
                </tr>
                <tr>
                  <th />
                  <th />
                </tr>
                <tr>
                  <th>Settlement Agent Receipt</th>
                  <th>
                    <LabeledInput
                      inputType="boolean"
                      inputName="settlement_agent_receipt"
                      inputValue={settlement_agent_receipt}
                      checkboxLabel={settlement_agent_receipt ? "Confirmed" : "Not Confirmed"}
                      onChange={this.onBooleanChange.bind(this)}
                    />
                  </th>
                </tr>
                <tr>
                  <th>Receipt Confirmed By</th>
                  <th>
                    <LabeledInput
                      inputName="receipt_confirmed_by"
                      inputValue={receipt_confirmed_by}
                      onChangeWithValue={this.onChange.bind(this, "receipt_confirmed_by")}
                    />
                  </th>
                </tr>
              </tbody>
            </Table>
          </div>

          <footer className={styles.footer}>
            <Button renderIcon={Save16} onClick={this.onSave.bind(this)}>
              Save changes
            </Button>
          </footer>
        </div>
      </HurdleView>
    );
  }
}

export default connect<any, any, any>(null, mapDispatchToProps)(FundingHurdleEditor);
